import Overlay from './overlay';
import { featureCollectionToTurfPolygons } from './createTurfPolygons';
import { generateFeatures } from './convert-to-geojson';
import * as turf from './turf';

/**
 * SalesDistrictsManager - Hanterar visning och hantering av säljdistrikt på kartan
 */
class SalesDistrictsManager {
    constructor() {
        this.salesDistricts = null;
        this.salesDistrictsOverlay = null;
        this.zoomListener = null;
        this.idleListener = null;
        this.hoverListener = null;
        this.mouseoutListener = null;
        this.currentDistrictId = null;
        this.lastHoveredDistrict = null; // För att undvika att logga samma distrikt flera gånger
    }

    /**
     * Toggle visning av säljdistrikt
     * @param {boolean} isVisible - Om säljdistrikt ska visas
     * @param {Object} mapState - MapStateManager-instans
     * @param {Function} mapService - MapService med fetchSpatialInfo
     * @param {string} type - District type
     * @param {string} scenarioId - Scenario ID
     * @param {Function} onLoadingChange - Callback för loading state (isLoading)
     * @param {string|null} currentDistrictId - ID för det nuvarande distriktet (används för framtida ID-matchning)
     * @param {string|null} currentDistrictName - Name för det nuvarande distriktet (används för matchning tills API:et stödjer id)
     * @returns {Promise<Object|null>} - Sales districts FeatureCollection eller null
     */
    async toggle(isVisible, mapState, mapService, type, scenarioId, onLoadingChange, currentDistrictId = null, currentDistrictName = null) {
        if (isVisible) {
            return await this.show(mapState, mapService, type, scenarioId, onLoadingChange, currentDistrictId, currentDistrictName);
        } else {
            this.hide(mapState);
            return null;
        }
    }

    /**
     * Visa säljdistrikt på kartan
     * @param {string|null} currentDistrictId - ID för det nuvarande distriktet (används för framtida ID-matchning)
     * @param {string|null} currentDistrictName - Name för det nuvarande distriktet (används för matchning tills API:et stödjer id)
     */
    async show(mapState, mapService, type, scenarioId, onLoadingChange, currentDistrictId = null, currentDistrictName = null) {
        onLoadingChange(true);
        
        // Spara currentDistrictId för användning i event listeners
        this.currentDistrictId = currentDistrictId;
        
        // Sätt currentDistrictId och currentDistrictName i mapState så att StyleManager kan använda det
        if (mapState.setCurrentDistrictId) {
            mapState.setCurrentDistrictId(currentDistrictId, currentDistrictName);
        }
        
        try {
            // Hämta districts från API
            const districts = await mapService.fetchSpatialInfo(type, scenarioId);

            // Konvertera till GeoJSON features och filtrera bort det nuvarande distriktet
            const features = [];
            districts.forEach((d, i) => {    
                if (d && d.geoJson) {
                    const district = JSON.parse(d.geoJson);
                    const districtName = d.name || district[0]?.properties?.name || null;
                    
                    // TODO: När API:et stödjer id i spatialinfo, ändra till att matcha på id istället för name
                    // Hoppa över det nuvarande distriktet - matcha på name tills API:et stödjer id
                    if (currentDistrictName && districtName && String(currentDistrictName).trim() === String(districtName).trim()) {
                        return; // Skippa detta distrikt
                    }
                    
                    // Get the color from the API response
                    const color = d.color || district[0].properties.color || '#ff0000';
                    
                    // Set the color in the properties
                    district[0].properties.color = color;
                    
                    // Store the district ID in properties för framtida användning när API:et stödjer det
                    // TODO: När API:et stödjer id i spatialinfo, spara districtId här
                    if (currentDistrictId) {
                        // För framtida ID-matchning
                        district[0].properties.districtId = currentDistrictId;
                    }
                    
                    features.push(generateFeatures(district[0], `${district[0].properties.name}${i}`, 'salesdistrict'));
                }
            });

            this.salesDistricts = {
                type: "FeatureCollection",
                features
            };
            
            // Lägg till på kartan (endast andra distrikt, inte det nuvarande)
            mapState.getMap().data.addGeoJson(this.salesDistricts);

            // Applicera styling på alla features (det nuvarande distriktet är redan filtrerat bort)
            features.forEach(district => {
                const feature = mapState.getMap().data.getFeatureById(district.id);
                if (!feature) return;
                
                const color = feature.getProperty('color');
                
                mapState.getMap().data.overrideStyle(feature, { 
                    strokeWeight: 0,
                    strokeColor: 'transparent',
                    fillOpacity: 0.5,
                    fillColor: color,
                    clickable: true, // Måste vara true för att mouseover ska fungera
                    visible: true,
                    zIndex: 1
                });
            });

            // Lägg till event listeners för zoom och idle
            this.zoomListener = mapState.getMaps().event.addListener(
                mapState.getMap(), 
                'zoom_changed', 
                () => {
                    this.removeLabels();
                }
            );

            this.idleListener = mapState.getMaps().event.addListener(
                mapState.getMap(), 
                'idle', 
                () => {
                    this.removeLabels();
                    this.createLabels(mapState, this.currentDistrictId);
                    // Sätt upp hover-listener efter att map är idle och all styling är klar
                    this.setupHoverListener(mapState);
                }
            );

            // Sätt upp hover-listener initialt
            this.setupHoverListener(mapState);

            // Skapa initial labels
            this.createLabels(mapState, this.currentDistrictId);

            return this.salesDistricts;
        } finally {
            onLoadingChange(false);
        }
    }

    /**
     * Dölj säljdistrikt från kartan
     */
    hide(mapState) {
        // Ta bort event listeners
        if (this.zoomListener) {
            mapState.getMaps().event.removeListener(this.zoomListener);
            this.zoomListener = null;
        }
        
        if (this.idleListener) {
            mapState.getMaps().event.removeListener(this.idleListener);
            this.idleListener = null;
        }

        if (this.hoverListener) {
            mapState.getMaps().event.removeListener(this.hoverListener);
            this.hoverListener = null;
        }
        
        if (this.mouseoutListener) {
            mapState.getMaps().event.removeListener(this.mouseoutListener);
            this.mouseoutListener = null;
        }
        
        this.lastHoveredDistrict = null;

        // Ta bort features från kartan
        mapState.getMap().data.forEach((feature) => {
            if (feature.getProperty('type') === 'salesdistrict') {
                mapState.getMap().data.remove(feature);
            }
        });

        // Ta bort labels
        this.removeLabels();
    }

    /**
     * Skapa labels för säljdistrikt
     * @param {Object} mapState - MapStateManager-instans
     * @param {string|null} currentDistrictId - ID för det nuvarande distriktet som ska exkluderas från labels
     */
    createLabels(mapState, currentDistrictId = null) {
        if (!this.salesDistricts) return;

        // Filtrera bort det nuvarande distriktet från labels om det finns
        let featuresForLabels = this.salesDistricts.features;
        if (currentDistrictId) {
            featuresForLabels = this.salesDistricts.features.filter(feature => {
                const featureDistrictId = feature.properties?.districtId;
                return !featureDistrictId || String(featureDistrictId) !== String(currentDistrictId);
            });
        }

        const filteredSalesDistricts = {
            ...this.salesDistricts,
            features: featuresForLabels
        };

        this.salesDistrictsOverlay = Overlay({ 
            polygons: featureCollectionToTurfPolygons(filteredSalesDistricts), 
            map: mapState.getMap(), 
            maps: mapState.getMaps(),
            ignoreArea: true,
            modifiers: {
                'overlay-label--districts': true
            }
        });
    }

    /**
     * Ta bort labels för säljdistrikt
     */
    removeLabels() {
        if (this.salesDistrictsOverlay) {
            this.salesDistrictsOverlay.setMap(null);
            this.salesDistrictsOverlay = null;
        }
    }

    /**
     * Sätt upp hover-listener för sales district-features
     */
    setupHoverListener(mapState) {
        // Ta bort gamla listeners om de finns
        if (this.hoverListener) {
            mapState.getMaps().event.removeListener(this.hoverListener);
        }
        if (this.mouseoutListener) {
            mapState.getMaps().event.removeListener(this.mouseoutListener);
        }
        
        // Helper-funktion för att hitta sales district under en position
        const findSalesDistrictAtPoint = (latLng) => {
            if (!this.salesDistricts || !this.salesDistricts.features) {
                return null;
            }
            
            const point = turf.point([latLng.lng(), latLng.lat()]);
            
            for (const featureData of this.salesDistricts.features) {
                try {
                    let polygon;
                    if (featureData.geometry.type === 'GeometryCollection') {
                        polygon = turf.multiPolygon(featureData.geometry.geometries.map(g => g.coordinates));
                    } else {
                        polygon = turf.polygon(featureData.geometry.coordinates);
                    }
                    
                    if (turf.booleanPointInPolygon(point, polygon)) {
                        return featureData.properties?.name;
                    }
                } catch (error) {
                    // Ignorera fel
                }
            }
            return null;
        };
        
        // Mouseover: när en feature får mouseover, kolla om det finns en sales district under
        this.hoverListener = mapState.getMap().data.addListener('mouseover', (event) => {
            const feature = event.feature;
            if (!feature || !event.latLng) return;
            
            const type = feature.getProperty('type');
            let districtName = null;
            
            // Om det är en sales district direkt, använd den
            if (type === 'salesdistrict') {
                districtName = feature.getProperty('name');
            } else {
                // Annars, kolla om det finns en sales district under denna position
                districtName = findSalesDistrictAtPoint(event.latLng);
            }
            
            // Logga om det är ett nytt distrikt
            if (districtName && this.lastHoveredDistrict !== districtName) {
                console.log('[SalesDistrict hover]', districtName);
                this.lastHoveredDistrict = districtName;
            }
        });
        
        // Mouseout: när musen lämnar en feature
        this.mouseoutListener = mapState.getMap().data.addListener('mouseout', (event) => {
            if (this.lastHoveredDistrict !== null) {
                console.log('[SalesDistrict mouseout]', this.lastHoveredDistrict);
                this.lastHoveredDistrict = null;
            }
        });
    }

    /**
     * Hämta aktuella sales districts
     */
    getSalesDistricts() {
        return this.salesDistricts;
    }
}

const salesDistrictsManagerInstance = new SalesDistrictsManager();
export default salesDistrictsManagerInstance;

